import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Filter ΔP — pleated media, many upstream arrows / fewer downstream, gauges on each side. */
export function DiagramFilterPressureDrop() {
  const dy = 95, dh = 80;
  const cy = dy + dh / 2;

  // Filter element
  const fx = 145, fw = 36;

  // Upstream / downstream duct extents
  const upX = 30, upW = fx - upX;
  const dnX = fx + fw, dnW = 320 - dnX;

  return (
    <DiagramCanvas>
      {/* Upstream duct */}
      <Rect x={upX} y={dy} width={upW} height={dh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />
      {/* Downstream duct */}
      <Rect x={dnX} y={dy} width={dnW} height={dh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />

      {/* Filter housing */}
      <Rect x={fx} y={dy - 6} width={fw} height={dh + 12} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* Pleated media — V-folds */}
      {Array.from({ length: 8 }).map((_, i) => {
        const yp = dy + 2 + (dh - 4) * (i / 8);
        const yp2 = dy + 2 + (dh - 4) * ((i + 0.5) / 8);
        return (
          <G key={i}>
            <Line x1={fx + 2} y1={yp} x2={fx + fw - 2} y2={yp2} stroke={DT.stroke} strokeWidth="0.8" opacity={0.7} />
            <Line x1={fx + 2} y1={yp2} x2={fx + fw - 2} y2={yp + (dh - 4) / 8} stroke={DT.stroke} strokeWidth="0.8" opacity={0.7} />
          </G>
        );
      })}

      {/* Many upstream arrows (high pressure pushing in) */}
      {[dy + 12, dy + 28, dy + 44, dy + 60, dy + 72].map((yp, i) => (
        <G key={`u${i}`}>
          <Line x1={upX + 12} y1={yp} x2={fx - 4} y2={yp} stroke={DT.arrow} strokeWidth="1.6" />
          <Polygon points={`${fx - 4},${yp} ${fx - 10},${yp - 3.5} ${fx - 10},${yp + 3.5}`} fill={DT.arrow} />
        </G>
      ))}
      {/* Fewer / shorter downstream arrows */}
      {[dy + 22, dy + 50].map((yp, i) => (
        <G key={`d${i}`}>
          <Line x1={dnX + 6} y1={yp} x2={dnX + 28} y2={yp} stroke={DT.arrow} strokeWidth="1.2" opacity={0.7} />
          <Polygon points={`${dnX + 28},${yp} ${dnX + 23},${yp - 3} ${dnX + 23},${yp + 3}`} fill={DT.arrow} opacity={0.7} />
        </G>
      ))}

      {/* Pressure tap → gauge upstream */}
      <Line x1={upX + 70} y1={dy} x2={upX + 70} y2={dy - 18} stroke={DT.leader} strokeWidth="1.2" />
      <Circle cx={upX + 70} cy={dy - 30} r={11} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <Line x1={upX + 70} y1={dy - 30} x2={upX + 64} y2={dy - 36} stroke={DT.varColor} strokeWidth="1.5" />
      <Circle cx={upX + 70} cy={dy - 30} r={1.5} fill={DT.varColor} />
      <SvgText x={upX + 70} y={dy - 44} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">P₁</SvgText>

      {/* Pressure tap → gauge downstream */}
      <Line x1={dnX + 60} y1={dy} x2={dnX + 60} y2={dy - 18} stroke={DT.leader} strokeWidth="1.2" />
      <Circle cx={dnX + 60} cy={dy - 30} r={11} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <Line x1={dnX + 60} y1={dy - 30} x2={dnX + 60 + 5} y2={dy - 33} stroke={DT.varColor} strokeWidth="1.5" />
      <Circle cx={dnX + 60} cy={dy - 30} r={1.5} fill={DT.varColor} />
      <SvgText x={dnX + 60} y={dy - 44} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">P₂</SvgText>

      {/* ΔP double-headed arrow between gauges */}
      <Line x1={upX + 70} y1={dy - 56} x2={dnX + 60} y2={dy - 56} stroke={DT.varColor} strokeWidth="1" />
      <Polygon points={`${upX + 70},${dy - 56} ${upX + 76},${dy - 53} ${upX + 76},${dy - 59}`} fill={DT.varColor} />
      <Polygon points={`${dnX + 60},${dy - 56} ${dnX + 54},${dy - 53} ${dnX + 54},${dy - 59}`} fill={DT.varColor} />
      <SvgText x={(upX + 70 + dnX + 60) / 2} y={dy - 60} fill={DT.varColor}
        fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔP</SvgText>

      {/* Labels */}
      <LeaderLine x1={fx + fw / 2} y1={dy + dh} x2={fx + fw / 2} y2={215} varName="Filter" desc="pleated media" anchor="middle" />
      <LeaderLine x1={upX + 30} y1={dy + dh - 8} x2={20} y2={210} varName="P₁" desc="upstream" anchor="start" />
      <LeaderLine x1={dnX + 40} y1={dy + dh - 8} x2={310} y2={210} varName="P₂" desc="downstream" anchor="end" />

      <FormulaTag formula="ΔP_filter = P₁ − P₂   (in. w.g.)" />
    </DiagramCanvas>
  );
}
